/**
 * Canonical payment-method resolver.
 *
 * Settlement type and monetary method are different questions.
 * Klippekort is a settlement type. Cash, bank, card, MobilePay, and
 * invoice are monetary methods on an invoice.
 *
 * Clinic toggles live in clinics.booking_payment_methods:
 * card, mobilepay, punch_card, invoice.
 * Cash and bank are not in that object. Authorized staff can always
 * register them. Patients cannot. Calendar, appointment details,
 * invoice, and finance all call this function.
 */

import {
  paymentMethodIsEnabled,
  type BookingPaymentMethods,
} from '@/lib/bookingPolicies';

export type MonetaryPaymentMethod =
  | 'cash'
  | 'bank'
  | 'card'
  | 'mobilepay'
  | 'payment_link'
  | 'invoice';

export type PaymentAudience = 'staff' | 'patient';

export interface PaymentMethodResolverInput {
  methods: BookingPaymentMethods;
  audience: PaymentAudience;
  clinioPayAvailable: boolean;
  canRedeemPunchCard?: boolean;
  canSettleInvoice?: boolean;
  /** Staff permission to type a cash/bank amount and date. Patients never can. */
  canRecordManualPayment?: boolean;
  applicablePunchCards?: Array<{ remaining_clips: number }>;
  chargeAmountCents: number;
  /** When an invoice already exists, this is the remaining balance. */
  outstandingCents?: number | null;
  invoiceStatus?: string | null;
  hasPunchSettlement?: boolean;
}

export interface ResolvedPaymentOptions {
  punchCard: boolean;
  monetaryMethods: MonetaryPaymentMethod[];
}

const OPEN_INVOICE = new Set(['issued', 'partial', 'overdue', 'expired_pay_link']);

function hasMoneyToCollect(input: PaymentMethodResolverInput): boolean {
  if (input.outstandingCents != null) return input.outstandingCents > 0;
  return input.chargeAmountCents > 0;
}

function invoiceBlocksMoney(status: string | null | undefined): boolean {
  return status === 'paid' || status === 'void' || status === 'cancelled' || status === 'refunded';
}

/**
 * Cash and bank stay available to authorized staff because the clinic
 * payment-method settings do not include them. They are not appended
 * by the calendar.
 */
export function resolvePaymentOptions(input: PaymentMethodResolverInput): ResolvedPaymentOptions {
  if (input.hasPunchSettlement || invoiceBlocksMoney(input.invoiceStatus)) {
    return { punchCard: false, monetaryMethods: [] };
  }

  const staff = input.audience === 'staff';
  const canRedeem = input.canRedeemPunchCard !== false;
  const canInvoice = input.canSettleInvoice !== false;
  const canManual = staff && input.canRecordManualPayment !== false && canInvoice;
  const cards = input.applicablePunchCards ?? [];
  const openInvoice = Boolean(input.invoiceStatus && OPEN_INVOICE.has(input.invoiceStatus));
  const collected = hasMoneyToCollect(input);

  const punchCard =
    canRedeem &&
    paymentMethodIsEnabled(input.methods, 'punch_card') &&
    cards.some((card) => card.remaining_clips > 0) &&
    !openInvoice &&
    input.invoiceStatus !== 'partial';

  const monetaryMethods: MonetaryPaymentMethod[] = [];
  if (canInvoice && collected) {
    if (canManual) {
      monetaryMethods.push('cash', 'bank');
    }
    if (input.clinioPayAvailable && paymentMethodIsEnabled(input.methods, 'card')) {
      monetaryMethods.push('card');
    }
    // Staff always see MobilePay when the clinic toggle is on. Choosing it
    // requests payment, and explains the gap if Clinio Pay is not active.
    // Patients only see it when Clinio Pay can actually take the payment.
    if (
      paymentMethodIsEnabled(input.methods, 'mobilepay') &&
      (staff || input.clinioPayAvailable)
    ) {
      monetaryMethods.push('mobilepay');
    }
    // A payment link is another request on the same invoice. An existing
    // request does not remove it, and an open invoice does not either.
    if (staff && input.clinioPayAvailable) {
      monetaryMethods.push('payment_link');
    }
    if (staff && paymentMethodIsEnabled(input.methods, 'invoice')) {
      monetaryMethods.push('invoice');
    }
  }

  if (!staff) {
    return {
      punchCard: false,
      monetaryMethods: monetaryMethods.filter((method) => method === 'card' || method === 'mobilepay'),
    };
  }

  return { punchCard, monetaryMethods };
}

export function isProviderPaymentMethod(
  method: MonetaryPaymentMethod,
): method is 'card' | 'mobilepay' {
  return method === 'card' || method === 'mobilepay';
}

export function isManualPaymentMethod(
  method: MonetaryPaymentMethod,
): method is 'cash' | 'bank' {
  return method === 'cash' || method === 'bank';
}
